import { StandaloneMarkdown } from "./markdown-text";
import chatEvents from '../../lib/chatEvents';
// Import VoiceContext directly for safe optional access (no throw if missing)
import { VoiceContext } from '../../lib/voice/VoiceProvider';
import {
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useMessageRuntime,
} from "@assistant-ui/react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useState, useRef, useContext, useEffect, createContext, memo, useCallback } from "react";
import { useStreamingText } from "../../hooks/useStreamingText";
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import MicIcon from "@mui/icons-material/Mic";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";

// Create local contexts that will be provided by parent components
export const FilesContext = createContext();
export const MessageFilesContext = createContext();
export const EmbeddedConfigContext = createContext();
// Context for shared thread info - provides user lookup for message attribution
// { isSharedThread: boolean, companyUsers: [{id, email, display_name}], currentUserId: string }
export const SharedThreadContext = createContext();

// Global typewriter state - allows Composer to know if typewriter is still animating
// and to skip to end when stop is pressed
const typewriterState = {
  isAnimating: false,
  skipToEnd: () => {},
};

// Shared copy function for message content (handles charts by converting canvas to image)
async function copyMessageContent(contentElem) {
  if (!contentElem) return false;

  try {
    // Check if this message contains a chart (canvas element)
    const canvasElements = contentElem.querySelectorAll('canvas');

    if (canvasElements.length > 0) {
      // Clone the content element to modify it without affecting the display
      const clonedContent = contentElem.cloneNode(true);

      // Convert each canvas to an image
      const clonedCanvases = clonedContent.querySelectorAll('canvas');
      canvasElements.forEach((originalCanvas, index) => {
        try {
          // Get the canvas as a PNG data URL
          const dataUrl = originalCanvas.toDataURL('image/png');

          // Create an img element
          const img = document.createElement('img');
          img.src = dataUrl;
          img.style.cssText = originalCanvas.style.cssText;
          img.alt = 'Chart visualization';

          // Replace canvas with img in the clone
          clonedCanvases[index].parentNode.replaceChild(img, clonedCanvases[index]);
        } catch (err) {
          console.error(`Failed to convert canvas ${index + 1}:`, err);
        }
      });

      // Now copy the modified HTML with images instead of canvases
      const selection = window.getSelection();
      const range = document.createRange();

      // Temporarily append the clone to the DOM (required for selection)
      clonedContent.style.position = 'absolute';
      clonedContent.style.left = '-9999px';
      document.body.appendChild(clonedContent);

      range.selectNodeContents(clonedContent);
      selection.removeAllRanges();
      selection.addRange(range);

      const successful = document.execCommand('copy');

      // Clean up
      selection.removeAllRanges();
      document.body.removeChild(clonedContent);

      if (!successful) {
        throw new Error('execCommand copy failed');
      }
    } else {
      // No canvas elements - use normal copy
      const selection = window.getSelection();
      const range = document.createRange();

      range.selectNodeContents(contentElem);
      selection.removeAllRanges();
      selection.addRange(range);

      const successful = document.execCommand('copy');
      selection.removeAllRanges();

      if (!successful) {
        throw new Error('execCommand copy failed');
      }
    }
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    // Fallback to plain text
    try {
      const text = contentElem.textContent.trim();
      await navigator.clipboard.writeText(text);
      return true;
    } catch (fallbackErr) {
      console.error('Fallback copy also failed:', fallbackErr);
      return false;
    }
  }
}

// Helper to get user display name from SharedThreadContext
// Returns author name for shared threads, null for non-shared threads
function useMessageAuthor(userId, messageRole) {
  const sharedThreadInfo = useContext(SharedThreadContext);
  if (!sharedThreadInfo?.isSharedThread) return null;

  // Assistant messages don't have authors (they're from the AI)
  if (messageRole === 'assistant') return null;

  // User messages without user_id (old messages before we tracked this)
  if (!userId) return null;

  const { companyUsers, currentUserId } = sharedThreadInfo;

  if (userId === currentUserId) return 'You';

  const user = companyUsers?.find(u => u.id === userId);
  if (user) {
    return user.display_name || user.email?.split('@')[0] || 'Unknown';
  }

  // User not in company users list (could be from impersonation or deleted user)
  return 'Unknown';
}

// Static component for rendering historical messages (not using runtime)
// Returns array of elements directly (no wrapper div) so :last-child CSS works
function HistoryMessages({ messages }) {
  // Subscribe to SharedThreadContext to re-render when companyUsers loads
  const sharedThreadInfo = useContext(SharedThreadContext);

  if (!messages || messages.length === 0) return null;

  return (
    <>
      {messages.map((msg, index) => (
        msg.role === 'user' ? (
          <HistoryUserMessage key={msg.id || index} id={msg.id} content={msg.content} attachments={msg.attachments || []} userId={msg.user_id} />
        ) : (
          <HistoryAssistantMessage key={msg.id || index} id={msg.id} content={msg.content} metadata={msg.metadata} userId={msg.user_id} />
        )
      ))}
    </>
  );
}

function HistoryUserMessage({ id, content, attachments = [], userId }) {
  const embeddedConfig = useContext(EmbeddedConfigContext);
  const API_URL = embeddedConfig?.apiUrl || window.INSIGHT_CONFIG?.API_URL || 'http://localhost:8000';
  const authorName = useMessageAuthor(userId, 'user');

  // Normalize content to string - content can be string (old format) or array of parts (new format)
  const getTextContent = () => {
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) {
      return content
        .filter(part => part.type === 'text')
        .map(part => part.text)
        .join('\n');
    }
    return '';
  };
  const textContent = getTextContent();

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!confirm('Delete this message?')) return;
    if (!id) {
      alert('Cannot delete message - ID not found');
      return;
    }

    // Capture the message element before async operation (e.currentTarget becomes stale after await)
    const messageElement = e.currentTarget.closest('[data-role="user"]');

    try {
      const API_URL = embeddedConfig?.apiUrl || window.INSIGHT_CONFIG?.API_URL || 'http://localhost:8000';

      // Use different endpoint and auth for embedded vs full mode
      let response;
      if (embeddedConfig?.isEmbedded && embeddedConfig?.shareToken && embeddedConfig?.customGptId) {
        // Embedded mode: use public endpoint with share token
        response = await fetch(`${API_URL}/public/cme/${embeddedConfig.customGptId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'messages.delete',
            params: { message_id: id, mode: embeddedConfig.mode || 'full' },
            token: embeddedConfig.shareToken
          })
        });
      } else {
        // Full mode: use protected endpoint with auth token
        const token = localStorage.getItem('cme_token');
        response = await fetch(`${API_URL}/cme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            type: 'messages.delete',
            params: { message_id: id, mode: 'full' }
          })
        });
      }

      if (response.ok) {
        messageElement.remove();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.error || 'Failed to delete message');
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      alert('Failed to delete message');
    }
  };

  return (
    <Box
      className="chat-message-enter chat-message user-message"
      data-role="user"
      data-message-id={id}
      sx={{ display: 'flex', justifyContent: 'flex-end' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '75%' }}>
        <Paper
          elevation={0}
          sx={{
            bgcolor: 'action.hover',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          {/* File attachments - render before text */}
          {attachments.length > 0 && (
            <Box sx={{ p: 1.5, pb: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {attachments.map((att, index) => {
                const isImage = att.content_type && att.content_type.startsWith('image/');
                const imageUrl = `${API_URL}/attachments/${att.id}`;

                if (isImage) {
                  return (
                    <Box key={index} sx={{ overflow: 'hidden', borderRadius: 3 }}>
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={att.filename}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          objectFit: 'contain',
                          maxWidth: 250,
                          maxHeight: 200,
                        }}
                      />
                    </Box>
                  );
                } else {
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'primary.main',
                        p: 1,
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2">{att.filename}</Typography>
                    </Box>
                  );
                }
              })}
            </Box>
          )}
          <Box
            className="chat-user-text"
            sx={{
              color: 'text.primary',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              p: 1.5,
            }}
          >
            {textContent}
          </Box>
        </Paper>
        {/* Tools container (visible on hover) for user messages */}
        {id && (
          <Box
            className="message-tools-container"
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.5,
              opacity: 0,
              transition: 'opacity 0.2s',
            }}
          >
            {authorName && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  mr: 0.5,
                }}
              >
                By - {authorName}
              </Typography>
            )}
            <Tooltip title="Delete message">
              <IconButton
                onClick={handleDelete}
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function HistoryAssistantMessage({ id, content, metadata, userId }) {
  const contentRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const embeddedConfig = useContext(EmbeddedConfigContext);
  const API_URL = embeddedConfig?.apiUrl || window.INSIGHT_CONFIG?.API_URL || 'http://localhost:8000';
  const authorName = useMessageAuthor(userId, 'assistant');

  // Normalize content to string for legacy handling
  // Content can be: string (old format) or array of parts (new format)
  const getTextContent = () => {
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) {
      return content
        .filter(part => part.type === 'text')
        .map(part => part.text)
        .join('\n');
    }
    return '';
  };
  const textContent = getTextContent();

  // Handle chart rendering
  useEffect(() => {
    if (!contentRef.current || !metadata?.chart_html) return;
    if (!textContent.startsWith('[Chart:')) return;

    console.log(`📊 [CHART] Rendering chart from history: ${metadata.chart_id}`);

    // Extract script content and HTML separately
    const scriptMatch = metadata.chart_html.match(/<script>([\s\S]*?)<\/script>/);
    const htmlOnly = metadata.chart_html.replace(/<script>[\s\S]*?<\/script>/, '');

    // Insert HTML
    contentRef.current.innerHTML = htmlOnly;

    // Execute script manually after DOM update
    if (scriptMatch && scriptMatch[1]) {
      setTimeout(() => {
        try {
          // Destroy any existing chart on this canvas first
          const canvas = contentRef.current.querySelector('canvas');
          if (canvas && window.Chart) {
            const existingChart = window.Chart.getChart(canvas);
            if (existingChart) {
              existingChart.destroy();
            }
          }
          eval(scriptMatch[1]);
          console.log(`✅ [CHART] Rendered ${metadata.chart_type} chart from history`);
        } catch (error) {
          console.error(`❌ [CHART] Error initializing chart from history:`, error);
        }
      }, 0);
    }
  }, [content, metadata]);

  const handleCopy = async (e) => {
    e.stopPropagation();
    const messageRoot = e.currentTarget.closest('[data-role="assistant"]');
    const contentElem = messageRoot.querySelector('.chat-assistant-text');
    const success = await copyMessageContent(contentElem);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!confirm('Delete this message?')) return;
    if (!id) {
      alert('Cannot delete message - ID not found');
      return;
    }

    // Capture the message element before async operation (e.currentTarget becomes stale after await)
    const messageElement = e.currentTarget.closest('[data-role="assistant"]');

    try {
      const API_URL = embeddedConfig?.apiUrl || window.INSIGHT_CONFIG?.API_URL || 'http://localhost:8000';

      // Use different endpoint and auth for embedded vs full mode
      let response;
      if (embeddedConfig?.isEmbedded && embeddedConfig?.shareToken && embeddedConfig?.customGptId) {
        // Embedded mode: use public endpoint with share token
        response = await fetch(`${API_URL}/public/cme/${embeddedConfig.customGptId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'messages.delete',
            params: { message_id: id, mode: embeddedConfig.mode || 'full' },
            token: embeddedConfig.shareToken
          })
        });
      } else {
        // Full mode: use protected endpoint with auth token
        const token = localStorage.getItem('cme_token');
        response = await fetch(`${API_URL}/cme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            type: 'messages.delete',
            params: { message_id: id, mode: 'single' }
          })
        });
      }

      if (response.ok) {
        messageElement.remove();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.error || 'Failed to delete message');
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      alert('Failed to delete message');
    }
  };

  // Check if this is a chart message
  const isChart = textContent.startsWith('[Chart:') && metadata?.chart_html;

  // Check if content has images (new format)
  const hasImages = Array.isArray(content) && content.some(part => part.type === 'image');

  // Render content parts (handles both text and images)
  const renderContentParts = () => {
    if (typeof content === 'string') {
      return <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown>;
    }

    if (Array.isArray(content)) {
      return content.map((part, index) => {
        if (part.type === 'text') {
          return <Markdown key={index} remarkPlugins={[remarkGfm]}>{part.text}</Markdown>;
        }
        if (part.type === 'image' && part.attachment_id) {
          const imageUrl = `${API_URL}/attachments/${part.attachment_id}`;
          return (
            <Box key={index} sx={{ my: 2 }}>
              <Box
                component="img"
                src={imageUrl}
                alt="Generated image"
                sx={{
                  maxWidth: '100%',
                  maxHeight: 512,
                  borderRadius: 2,
                  boxShadow: 2,
                }}
              />
            </Box>
          );
        }
        return null;
      });
    }

    return null;
  };

  return (
    <Box
      className="chat-message-enter chat-message assistant-message"
      data-role="assistant"
      data-message-id={id}
      sx={{ display: 'flex' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box
          ref={contentRef}
          className="chat-assistant-text aui-md"
          sx={{
            color: 'text.primary',
            maxWidth: 'none',
            wordBreak: 'break-word',
          }}
        >
          {/* Only render content if not a chart (chart will be rendered via useEffect) */}
          {!isChart && renderContentParts()}
        </Box>
        {/* Tools container (visible on hover) for assistant messages */}
        {id && (
          <Box
            className="message-tools-container"
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.5,
              opacity: 0,
              transition: 'opacity 0.2s',
            }}
          >
            <Tooltip title={copied ? "Copied!" : "Copy message"}>
              <IconButton
                onClick={handleCopy}
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
              >
                {copied ? <CheckIcon /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete message">
              <IconButton
                onClick={handleDelete}
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
            {authorName && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  ml: 0.5,
                }}
              >
                By - {authorName}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export const Thread = ({ historyMessages = [], conversationStartersSlot = null }) => {
  // Track which history array we've scrolled for (by reference)
  const scrolledForRef = useRef(null);

  // Scroll to bottom once when history with messages is loaded
  useEffect(() => {
    // Skip if no messages or already scrolled for this exact array
    if (!historyMessages || historyMessages.length === 0) return;
    if (scrolledForRef.current === historyMessages) return;

    // Mark this array as handled
    scrolledForRef.current = historyMessages;

    const viewport = document.getElementById('chatScrollContainer');
    if (!viewport) return;

    const scrollToBottom = () => {
      if (viewport.scrollHeight > viewport.clientHeight) {
        viewport.scrollTop = viewport.scrollHeight;
      }
    };

    // Find all images in the viewport
    const images = viewport.querySelectorAll('img');

    if (images.length === 0) {
      // No images - scroll immediately
      scrollToBottom();
      return;
    }

    // Wait for all images to load before scrolling
    let loadedCount = 0;
    const totalCount = images.length;
    let hasScrolled = false;

    const checkAndScroll = () => {
      loadedCount++;
      if (loadedCount >= totalCount && !hasScrolled) {
        hasScrolled = true;
        scrollToBottom();
      }
    };

    images.forEach(img => {
      if (img.complete) {
        checkAndScroll();
      } else {
        img.addEventListener('load', checkAndScroll, { once: true });
        img.addEventListener('error', checkAndScroll, { once: true });
      }
    });

    // Fallback: scroll after 2 seconds regardless
    const fallbackTimer = setTimeout(() => {
      if (!hasScrolled) {
        hasScrolled = true;
        scrollToBottom();
      }
    }, 2000);

    return () => clearTimeout(fallbackTimer);
  }, [historyMessages]);

  return (
    <ThreadPrimitive.Root id="chat-area" className="chat-area">
      {/* Messages - IDs match EMBED_DOCUMENTATION.md selectors */}
      <ThreadPrimitive.Viewport id="chatScrollContainer" className="chat-viewport">
        <div id="chatContainer" className="chat-messages-content">
          <HistoryMessages messages={historyMessages} />
          <ThreadPrimitive.Messages
            components={{
              UserMessage,
              AssistantMessage,
            }}
          />
        </div>
      </ThreadPrimitive.Viewport>

      {/* Starters - IDs match EMBED_DOCUMENTATION.md selectors */}
      {conversationStartersSlot && (
        <div id="conversationStarters" className="chat-starters">
          <div id="startersContainer" className="chat-starters-inner">
            {conversationStartersSlot}
          </div>
        </div>
      )}

      {/* Input */}
      <div id="chat-input" className="chat-input">
        <div className="chat-input-content">
          <Composer />
          <div
            id="chat-disclaimer"
            className="chat-disclaimer"
            style={{
              fontSize: '12px',
              color: 'var(--text-tertiary, #8e8ea0)',
              textAlign: 'center',
              padding: '6px 12px 4px',
              opacity: 0.75,
              userSelect: 'none',
            }}
          >
            AI can and will make mistakes. Please verify important information.
          </div>
        </div>
      </div>
    </ThreadPrimitive.Root>
  );
};

const Composer = () => {
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const filesContext = useContext(FilesContext);
  const { selectedFiles, setSelectedFiles } = filesContext || { selectedFiles: [], setSelectedFiles: () => {} };
  const [browserListening, setBrowserListening] = useState(false);
  const [isTypewriterAnimating, setIsTypewriterAnimating] = useState(false);
  const [sttLang, setSttLang] = useState('');  // '' = auto-detect
  const [showLangMenu, setShowLangMenu] = useState(false);
  const micBtnRef = useRef(null);
  const longPressedRef = useRef(false);

  // Get embedded config if available
  const embeddedConfig = useContext(EmbeddedConfigContext);

  // VoiceProvider (optional — null if no VoiceProvider wraps this component)
  const voice = useContext(VoiceContext);
  const isListening = voice ? voice.listening : browserListening;

  // Poll typewriter state to know when to show stop button
  useEffect(() => {
    const interval = setInterval(() => {
      if (typewriterState.isAnimating !== isTypewriterAnimating) {
        setIsTypewriterAnimating(typewriterState.isAnimating);
      }
    }, 50); // Check every 50ms
    return () => clearInterval(interval);
  }, [isTypewriterAnimating]);

  // iPad keyboard dismiss fix - when keyboard hides via keyboard button, scroll to fix viewport
  // Only apply to iPad, not iPhone (iPhone handles this differently)
  useEffect(() => {
    const isIPad = /iPad/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 && window.innerWidth >= 768);

    if (!isIPad) return;

    // Use visualViewport API to detect keyboard dismiss
    const handleViewportResize = () => {
      // When keyboard closes, visualViewport height increases back to window height
      // Scroll to reset the viewport position
      if (window.visualViewport && window.visualViewport.height >= window.innerHeight * 0.9) {
        // Small delay to let iOS finish its animation
        setTimeout(() => {
          window.scrollTo(0, 0);
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
        }, 100);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
      return () => {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
      };
    }
  }, []);

  // Handle stop button click when typewriter is animating
  const handleStopTypewriter = () => {
    if (typewriterState.skipToEnd) {
      typewriterState.skipToEnd();
      setIsTypewriterAnimating(false);
    }
    // Focus the textarea after stopping
    const textarea = document.getElementById('messageInput');
    if (textarea) {
      textarea.focus();
    }
  };

  // Focus textarea (used when clicking disabled send button)
  const focusInput = () => {
    setTimeout(() => {
      const textarea = document.getElementById('messageInput');
      if (textarea) {
        textarea.focus();
      }
    }, 50);
  };

  // Handle stop/cancel button - remove empty "Thinking..." message and focus input
  const handleStopAndFocus = () => {
    setTimeout(() => {
      // Remove empty assistant message if it only has "Thinking..." (no real content)
      const assistantMessages = document.querySelectorAll('[data-role="assistant"]');
      if (assistantMessages.length > 0) {
        const lastAssistant = assistantMessages[assistantMessages.length - 1];
        const contentContainer = lastAssistant.querySelector('.assistant-content-container');
        if (contentContainer) {
          // Check if it only has the thinking span (no real content)
          const thinkingSpan = contentContainer.querySelector('.thinking-pulse');
          if (thinkingSpan && contentContainer.children.length === 1) {
            lastAssistant.remove();
          }
        }
      }

      const textarea = document.getElementById('messageInput');
      if (textarea) {
        textarea.focus();
      }
    }, 50); // Small delay to let the cancel action complete
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
    // Reset input
    e.target.value = '';
  };

  const handlePaste = async (e) => {
    // Disable paste if hidePaste is true in embedded mode
    if (embeddedConfig?.hidePaste) {
      return;
    }

    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles = [];
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const blob = item.getAsFile();
        if (blob) {
          const file = new File([blob], `pasted-image-${Date.now()}.png`, { type: item.type });
          imageFiles.push(file);
        }
      }
    }

    if (imageFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...imageFiles]);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Voice input setup — uses SttPlayer (via chatEvents) if microphone tool configured, else browser SpeechRecognition
  const usingSttPlayer = !!voice || !!embeddedConfig?.hasMicrophone;

  // Helper: set textarea value programmatically
  function setTextareaValue(text) {
    if (!inputRef.current) return;
    const textarea = inputRef.current.querySelector('textarea');
    if (!textarea) return;
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLTextAreaElement.prototype, 'value'
    ).set;
    nativeInputValueSetter.call(textarea, text);
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // VoiceProvider STT: watch transcript and auto-send on final
  const prevVoiceTranscriptRef = useRef('');
  useEffect(() => {
    if (!voice) return;
    if (voice.transcript && voice.transcript !== prevVoiceTranscriptRef.current) {
      prevVoiceTranscriptRef.current = voice.transcript;
      setTextareaValue(voice.transcript);
      // Auto-send when listening stops with a transcript (final)
      if (!voice.listening && voice.transcript.trim()) {
        setTimeout(() => {
          const sendBtn = document.getElementById('sendButton');
          if (sendBtn) sendBtn.click();
        }, 100);
      }
    }
    if (!voice.transcript) prevVoiceTranscriptRef.current = '';
  }, [voice?.transcript, voice?.listening]);

  // Browser SpeechRecognition fallback (when no VoiceProvider and no microphone tool)
  useEffect(() => {
    if (voice || usingSttPlayer) return; // VoiceProvider or SttPlayer handles it

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 1;

      recognition.onstart = () => { setBrowserListening(true); };
      recognition.onend = () => { setBrowserListening(false); };

      recognition.onresult = (event) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) finalTranscript += event.results[i][0].transcript;
        }
        if (finalTranscript) setTextareaValue(finalTranscript);
      };

      recognition.onerror = (event) => {
        setBrowserListening(false);
        const msgs = {
          'no-speech': 'No speech detected. Please try again.',
          'audio-capture': 'No microphone found or microphone access denied.',
          'not-allowed': 'Microphone access denied. Please grant permission.',
          'network': 'Network error occurred during voice recognition.',
        };
        alert(msgs[event.error] || `Voice recognition error: ${event.error}`);
      };

      recognitionRef.current = recognition;
    }

    return () => { if (recognitionRef.current) recognitionRef.current.stop(); };
  }, [usingSttPlayer]);

  const handleMicClick = () => {
    setShowLangMenu(false);

    // VoiceProvider mode
    if (voice && usingSttPlayer) {
      if (voice.listening) voice.stopListening();
      else { console.log('[Thread] mic click, sttLang:', sttLang); voice.setLang(sttLang); voice.startListening(); }
      return;
    }

    // Browser fallback
    if (!recognitionRef.current) {
      alert('Voice recognition is not supported in this browser.');
      return;
    }
    if (browserListening) recognitionRef.current.stop();
    else {
      try { recognitionRef.current.start(); }
      catch (e) { console.error('Speech recognition error:', e); }
    }
  };

  const isReadonly = embeddedConfig?.isReadonly;
  const isMobile = typeof window !== 'undefined' && (window.innerWidth <= 768 || ('ontouchstart' in window && window.innerWidth <= 1024));
  const hasMicOrVoice = usingSttPlayer || embeddedConfig?.hasVoice;

  return (
    <ComposerPrimitive.Root id="chatForm" style={{ position: 'relative', display: 'flex', flexDirection: 'column', width: '100%', gap: '8px' }}>
      {/* Mobile: Big mic button floating above input */}
      {isMobile && !isReadonly && usingSttPlayer && (
        <Box sx={{
          position: 'absolute', top: -90, left: '50%', transform: 'translateX(-50%)',
          zIndex: 5,
        }}>
          <IconButton
            onClick={handleMicClick}
            disabled={voice?.connecting}
            sx={{
              width: 52, height: 52,
              bgcolor: voice?.connecting ? 'warning.main' : isListening ? 'error.main' : 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: voice?.connecting ? 'warning.dark' : isListening ? 'error.dark' : 'primary.dark' },
              boxShadow: isListening ? '0 0 16px rgba(244,67,54,0.5)' : voice?.connecting ? '0 0 16px rgba(255,167,38,0.5)' : 2,
              animation: (isListening || voice?.connecting) ? 'pulse 1.5s infinite' : 'none',
              '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } },
            }}
          >
            <MicIcon sx={{ fontSize: 28 }} />
          </IconButton>
        </Box>
      )}
      {/* File attachments preview - hidden in readonly mode */}
      {!isReadonly && selectedFiles.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {selectedFiles.map((file, index) => (
            <Box key={index} sx={{ position: 'relative', display: 'inline-block' }}>
              {file.type.startsWith('image/') ? (
                <Box
                  component="img"
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  sx={{
                    width: 64,
                    height: 64,
                    objectFit: 'cover',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: 'action.hover',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" fontWeight={500}>
                    {file.name.split('.').pop().toUpperCase()}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  fontSize: '0.65rem',
                  px: 0.5,
                  py: 0.25,
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {file.name}
              </Box>
              <IconButton
                size="small"
                onClick={() => removeFile(index)}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  width: 20,
                  height: 20,
                  bgcolor: 'error.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'error.dark' },
                }}
              >
                <CloseIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Input area */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          borderRadius: 6,
          border: 1,
          borderColor: isReadonly ? 'divider' : 'divider',
          bgcolor: isReadonly ? 'action.disabledBackground' : 'background.paper',
          transition: 'border-color 0.2s',
          minHeight: 48,
          py: 0.5,
          '&:focus-within': {
            borderColor: isReadonly ? 'divider' : 'primary.main',
          },
        }}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          accept="image/*,.pdf,.doc,.docx,.txt,.xlsx,.xls"
          multiple
        />
        {!isReadonly && !embeddedConfig?.hideFileUpload && (
          <Tooltip title="Attach files">
            <IconButton
              onClick={handleFileClick}
              sx={{ ml: 1, color: 'text.secondary' }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
        <Box
          ref={inputRef}
          sx={{
            flex: 1,
            py: 1,
            px: (!isReadonly && !embeddedConfig?.hideFileUpload) ? 1 : 2,
          }}
        >
          <ComposerPrimitive.Input
            id="messageInput"
            placeholder={isReadonly ? "Read-only mode" : "Type your message..."}
            style={{
              display: 'block',
              width: '100%',
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              resize: 'none',
              overflow: 'auto',
              margin: 0,
              fontSize: '1rem',
              lineHeight: 1.5,
              fontFamily: 'inherit',
              color: isReadonly ? 'inherit' : 'inherit',
              cursor: isReadonly ? 'not-allowed' : 'text',
            }}
            rows={1}
            maxRows={5}
            autoFocus={!isReadonly}
            onPaste={handlePaste}
            disabled={isReadonly}
          />
        </Box>
        {!isReadonly && (
          <>
            {/* Language selector — mobile: globe icon, desktop: hover on mic */}
            {isMobile && usingSttPlayer && (
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <Tooltip title={sttLang ? `Language: ${sttLang.toUpperCase()}` : 'Language: Auto'}>
                  <IconButton
                    onClick={() => setShowLangMenu(v => !v)}
                    sx={{ color: 'text.secondary', fontSize: '14px', width: 36, height: 36 }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                  </IconButton>
                </Tooltip>
                {showLangMenu && (
                  <Box sx={{
                    position: 'absolute', bottom: '100%', right: 0,
                    mb: 0.5, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 3,
                    border: '1px solid', borderColor: 'divider',
                    display: 'flex', flexWrap: 'wrap', gap: 0.25, p: 0.5, maxWidth: 200, zIndex: 100,
                  }}>
                    {[
                      { code: '', label: 'Auto' }, { code: 'en', label: 'EN' }, { code: 'it', label: 'IT' },
                      { code: 'fr', label: 'FR' }, { code: 'de', label: 'DE' }, { code: 'es', label: 'ES' },
                      { code: 'pt', label: 'PT' }, { code: 'ja', label: 'JA' }, { code: 'zh', label: 'ZH' },
                      { code: 'ko', label: 'KO' }, { code: 'ar', label: 'AR' }, { code: 'ru', label: 'RU' },
                    ].map(l => (
                      <Box key={l.code} onClick={(e) => { e.stopPropagation(); setSttLang(l.code); setShowLangMenu(false); if (voice && !voice.listening) { voice.setLang(l.code); voice.startListening(); } }}
                        sx={{
                          px: 0.75, py: 0.25, borderRadius: 0.5, cursor: 'pointer', fontSize: '11px',
                          fontWeight: sttLang === l.code ? 700 : 400,
                          bgcolor: sttLang === l.code ? 'primary.main' : 'transparent',
                          color: sttLang === l.code ? 'primary.contrastText' : 'text.secondary',
                          '&:hover': { bgcolor: sttLang === l.code ? 'primary.dark' : 'action.hover' },
                        }}
                      >{l.label}</Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
            {/* Speaker toggle */}
            {embeddedConfig?.hasVoice && (
              <Tooltip title={(voice ? voice.enabled : embeddedConfig.voiceEnabled) ? 'Disable voice output' : 'Enable voice output'}>
                <IconButton
                  onClick={() => voice ? voice.toggle() : embeddedConfig.onToggleVoice?.()}
                  sx={{
                    color: (voice ? voice.enabled : embeddedConfig.voiceEnabled) ? 'primary.main' : 'text.disabled',
                    transition: 'color 0.2s',
                  }}
                >
                  {(voice ? voice.enabled : embeddedConfig.voiceEnabled) ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
                      <line x1="23" y1="9" x2="17" y2="15" />
                      <line x1="17" y1="9" x2="23" y2="15" />
                    </svg>
                  )}
                </IconButton>
              </Tooltip>
            )}
            {/* Desktop: Mic button with hover language menu */}
            {!isMobile && (
            <Box
              ref={micBtnRef}
              sx={{ position: 'relative', display: 'inline-flex' }}
              onMouseEnter={() => { if (!isListening && usingSttPlayer) setShowLangMenu(true); }}
              onMouseLeave={() => setShowLangMenu(false)}
            >
              <Tooltip title={voice?.connecting ? 'Connecting microphone...' : isListening ? 'Listening... (click to stop)' : (sttLang ? `Voice input (${sttLang.toUpperCase()})` : 'Voice input (auto)')}>
                <span>
                  <IconButton
                    onClick={handleMicClick}
                    disabled={voice?.connecting}
                    sx={{
                      color: voice?.connecting ? 'warning.main' : isListening ? 'error.main' : 'text.secondary',
                      animation: (isListening || voice?.connecting) ? 'pulse 1.5s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.5 },
                      },
                    }}
                  >
                    <MicIcon />
                  </IconButton>
                </span>
              </Tooltip>
              {showLangMenu && (
                <Box sx={{
                  position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                  mb: 0.5, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 3,
                  border: '1px solid', borderColor: 'divider',
                  display: 'flex', flexWrap: 'wrap', gap: 0.25, p: 0.5, maxWidth: 200, zIndex: 100,
                }}>
                  {[
                    { code: '', label: 'Auto' },
                    { code: 'en', label: 'EN' },
                    { code: 'it', label: 'IT' },
                    { code: 'fr', label: 'FR' },
                    { code: 'de', label: 'DE' },
                    { code: 'es', label: 'ES' },
                    { code: 'pt', label: 'PT' },
                    { code: 'ja', label: 'JA' },
                    { code: 'zh', label: 'ZH' },
                    { code: 'ko', label: 'KO' },
                    { code: 'ar', label: 'AR' },
                    { code: 'ru', label: 'RU' },
                    { code: 'nl', label: 'NL' },
                    { code: 'hi', label: 'HI' },
                  ].map(l => (
                    <Box
                      key={l.code}
                      onClick={(e) => { e.stopPropagation(); setSttLang(l.code); setShowLangMenu(false); if (voice && !voice.listening) { voice.setLang(l.code); voice.startListening(); } }}
                      sx={{
                        px: 0.75, py: 0.25, borderRadius: 0.5, cursor: 'pointer',
                        fontSize: '11px', fontWeight: sttLang === l.code ? 700 : 400,
                        bgcolor: sttLang === l.code ? 'primary.main' : 'transparent',
                        color: sttLang === l.code ? 'primary.contrastText' : 'text.secondary',
                        '&:hover': { bgcolor: sttLang === l.code ? 'primary.dark' : 'action.hover' },
                      }}
                    >
                      {l.label}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            )}
            {/* Show Stop button when thread is running OR typewriter is animating */}
            <ThreadPrimitive.If running>
              <ComposerPrimitive.Cancel asChild>
                <IconButton
                  id="stopButton"
                  onClick={handleStopAndFocus}
                  sx={{
                    mr: 1,
                    bgcolor: 'grey.500',
                    color: 'white',
                    '&:hover': { bgcolor: 'grey.600' },
                  }}
                >
                  <StopIcon />
                </IconButton>
              </ComposerPrimitive.Cancel>
            </ThreadPrimitive.If>
            <ThreadPrimitive.If running={false}>
              {isTypewriterAnimating ? (
                <IconButton
                  id="stopButton"
                  onClick={handleStopTypewriter}
                  sx={{
                    mr: 1,
                    bgcolor: 'grey.500',
                    color: 'white',
                    '&:hover': { bgcolor: 'grey.600' },
                  }}
                >
                  <StopIcon />
                </IconButton>
              ) : (
                <Box
                  onClick={focusInput}
                  sx={{ display: 'inline-flex' }}
                >
                  <ComposerPrimitive.Send asChild>
                    <IconButton
                      id="sendButton"
                      onClick={() => {
                        // Blur input to close keyboard after sending on touch devices (mobile/tablet)
                        // Use setTimeout to blur after assistant-ui's internal focus logic
                        const isTouchDevice = 'ontouchstart' in window ||
                          navigator.maxTouchPoints > 0 ||
                          /iPad|iPhone|iPod|Android/i.test(navigator.userAgent);
                        if (isTouchDevice) {
                          setTimeout(() => {
                            const textarea = document.getElementById('messageInput');
                            if (textarea) {
                              textarea.blur();
                            }
                          }, 100);
                        }
                      }}
                      sx={{
                        mr: 1,
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' },
                        '&:disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                      }}
                    >
                      <SendIcon />
                    </IconButton>
                  </ComposerPrimitive.Send>
                </Box>
              )}
            </ThreadPrimitive.If>
          </>
        )}
      </Box>
    </ComposerPrimitive.Root>
  );
};

// Track initialized charts globally to prevent duplicate creation across re-renders
const initializedCharts = new Map(); // chartId -> { chart: Chart instance, html: string }

// Component to render chart HTML and execute its script
const ChartRendererImpl = ({ chartHtml, chartId }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !chartHtml || !chartId) {
      return;
    }

    // Check if this exact chart is already initialized
    const existing = initializedCharts.get(chartId);
    if (existing && existing.html === chartHtml) {
      // Chart already exists, just make sure the HTML is there
      const htmlOnly = chartHtml.replace(/<script>[\s\S]*?<\/script>/, '');
      containerRef.current.innerHTML = htmlOnly;
      return;
    }

    // Destroy existing chart if it exists
    if (existing && existing.chart && typeof existing.chart.destroy === 'function') {
      existing.chart.destroy();
      initializedCharts.delete(chartId);
    }

    // Extract script and HTML separately
    const scriptMatch = chartHtml.match(/<script>([\s\S]*?)<\/script>/);
    const htmlOnly = chartHtml.replace(/<script>[\s\S]*?<\/script>/, '');

    // Set the HTML content
    containerRef.current.innerHTML = htmlOnly;

    // Execute the script after a delay to let DOM update
    if (scriptMatch && scriptMatch[1]) {
      setTimeout(() => {
        // Double-check we haven't already initialized
        if (initializedCharts.has(chartId)) {
          return;
        }

        try {
          const canvas = document.getElementById(chartId);
          if (canvas && typeof Chart !== 'undefined') {
            // Store a placeholder immediately to prevent race conditions
            initializedCharts.set(chartId, { chart: null, html: chartHtml });

            // Modify the script to capture the chart instance
            const modifiedScript = scriptMatch[1].replace(
              /new Chart\(canvas,/,
              `window.__lastChart = new Chart(canvas,`
            );
            eval(modifiedScript);

            if (window.__lastChart) {
              initializedCharts.set(chartId, { chart: window.__lastChart, html: chartHtml });
              delete window.__lastChart;
            }
          }
        } catch (e) {
          console.error('Chart script error:', e);
          initializedCharts.delete(chartId);
        }
      }, 150);
    }

    // Cleanup on unmount
    return () => {
      // Don't destroy on unmount - the chart may be re-mounted
      // Only destroy when a new chart with different HTML is created
    };
  }, [chartId, chartHtml]);

  return <div ref={containerRef} />;
};

// Memoize to prevent unnecessary re-renders
const ChartRenderer = memo(ChartRendererImpl, (prevProps, nextProps) => {
  // Only re-render if chartId changes
  return prevProps.chartId === nextProps.chartId;
});

// TypewriterText - applies typewriter effect to streaming text only
const TypewriterText = ({ text }) => {
  const { displayedText, isAnimating, skipToEnd } = useStreamingText(text || '', 5);

  // Update global typewriter state so Composer can access it
  useEffect(() => {
    typewriterState.isAnimating = isAnimating;
    typewriterState.skipToEnd = skipToEnd;
  }, [isAnimating, skipToEnd]);

  if (!displayedText) return null;
  return <StandaloneMarkdown text={displayedText} />;
};

// StatusDisplay - shows status messages immediately (no typewriter)
const StatusDisplay = ({ statusType, text }) => {
  const statusColor = statusType === 'error' ? 'error.main' :
                     statusType === 'warning' ? 'warning.main' :
                     'text.secondary';

  return (
    <Typography
      sx={{
        color: statusColor,
        fontStyle: 'italic',
        animation: 'pulse 1.5s infinite',
        pb: 1.5,
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.5 },
        },
      }}
    >
      {text}
    </Typography>
  );
};

// ChartDisplay - renders charts immediately (no typewriter)
const ChartDisplay = ({ chartHtml, chartId }) => {
  return <ChartRenderer chartHtml={chartHtml} chartId={chartId} />;
};

// FileDownloadDisplay - renders file download button immediately
const FileDownloadDisplay = ({ filename, fileData, format, sizeBytes }) => {
  const [downloadState, setDownloadState] = useState('ready'); // 'ready', 'downloaded', 'failed'

  const fileIcon = format === 'excel' ? '📊' :
                   format === 'word' ? '📝' :
                   format === 'pdf' ? '📄' : '📋';

  const fileSizeKB = Math.round(sizeBytes / 1024);

  const handleDownload = () => {
    try {
      // Decode base64 and create blob
      const binaryString = atob(fileData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Determine MIME type
      const mimeTypes = {
        'excel': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'word': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'pdf': 'application/pdf',
        'csv': 'text/csv'
      };
      const mimeType = mimeTypes[format] || 'application/octet-stream';

      const blob = new Blob([bytes], { type: mimeType });
      const url = URL.createObjectURL(blob);

      // Create temporary link and trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      console.log(`✅ [FILE] Download triggered successfully`);
      setDownloadState('downloaded');
    } catch (error) {
      console.error(`❌ [FILE] Download failed:`, error);
      setDownloadState('failed');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        my: 1.5,
        p: 1.5,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        maxWidth: 360,
        boxShadow: 1,
      }}
    >
      <Typography sx={{ fontSize: '1.5rem' }}>{fileIcon}</Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontWeight: 500,
          fontSize: '0.875rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {filename}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {fileSizeKB} KB
        </Typography>
      </Box>
      <Box
        component="button"
        onClick={handleDownload}
        sx={{
          px: 2,
          py: 0.75,
          borderRadius: 1.5,
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.8rem',
          fontWeight: 600,
          bgcolor: downloadState === 'downloaded' ? 'success.main' :
                   downloadState === 'failed' ? 'error.main' : 'primary.main',
          color: 'white',
          '&:hover': {
            bgcolor: downloadState === 'downloaded' ? 'success.dark' :
                     downloadState === 'failed' ? 'error.dark' : 'primary.dark',
          },
        }}
      >
        {downloadState === 'downloaded' ? '✓ Done' :
         downloadState === 'failed' ? '✗ Error' : 'Download'}
      </Box>
    </Box>
  );
};

// ToolResultText - shows tool results immediately (no typewriter)
const ToolResultText = ({ text }) => {
  if (!text) return null;
  return <StandaloneMarkdown text={text} />;
};

// Legacy fallback: handles old-style embedded content (status markers, charts in text)
const LegacyTextHandler = ({ text }) => {
  if (!text) return null;

  // Check for embedded status marker (legacy format)
  const statusMatch = text.match(/__STATUS_(LOG|WARNING|ERROR)__:([^\n]+)/);
  if (statusMatch) {
    const statusType = statusMatch[1].toLowerCase();
    const statusText = statusMatch[2];
    const textWithoutStatus = text.replace(/__STATUS_(LOG|WARNING|ERROR)__:[^\n]+\n?/, '').trim();

    return (
      <>
        {textWithoutStatus && <TypewriterText text={textWithoutStatus} />}
        <StatusDisplay statusType={statusType} text={statusText} />
      </>
    );
  }

  // Check for embedded chart HTML (legacy format)
  const chartMatch = text.match(/<div class="chart-container"[\s\S]*?<\/script>\s*\n?/);
  if (chartMatch) {
    const chartHtml = chartMatch[0];
    const beforeChart = text.substring(0, chartMatch.index).trim();
    const afterChart = text.substring(chartMatch.index + chartHtml.length).trim();

    const idMatch = chartHtml.match(/id="(chart_[^"]+)"/);
    const chartId = idMatch ? idMatch[1] : `chart_${Date.now()}`;

    return (
      <>
        {beforeChart && <TypewriterText text={beforeChart} />}
        <ChartRenderer chartHtml={chartHtml} chartId={chartId} />
        {afterChart && <LegacyTextHandler text={afterChart} />}
      </>
    );
  }

  // Pure text - apply typewriter
  return <TypewriterText text={text} />;
};

// GeneratedImageDisplay - renders AI-generated images
const GeneratedImageDisplay = ({ data, mimeType }) => {
  const dataUrl = `data:${mimeType};base64,${data}`;

  return (
    <Box
      sx={{
        my: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <Box
        component="img"
        src={dataUrl}
        alt="Generated image"
        sx={{
          maxWidth: '100%',
          maxHeight: 512,
          borderRadius: 2,
          boxShadow: 2,
        }}
      />
    </Box>
  );
};

// Component to render a single part based on its type
const PartRenderer = ({ part }) => {
  if (!part) return null;

  switch (part.type) {
    case 'text':
      // Text content gets typewriter effect
      return <TypewriterText text={part.text} />;

    case 'status':
      // Status messages appear immediately (no typewriter, no markdown)
      return <StatusDisplay statusType={part.statusType} text={part.text} />;

    case 'chart':
      // Charts render immediately (no typewriter, no markdown)
      return <ChartDisplay chartHtml={part.chartHtml} chartId={part.chartId} />;

    case 'file_download':
      // File download - show download button immediately
      return <FileDownloadDisplay
        filename={part.filename}
        fileData={part.fileData}
        format={part.format}
        sizeBytes={part.sizeBytes}
      />;

    case 'tool-result':
      // Tool results appear immediately (no typewriter)
      return <ToolResultText text={part.text} />;

    case 'image':
      // AI-generated images - render immediately
      return <GeneratedImageDisplay data={part.data} mimeType={part.mime_type || 'image/png'} />;

    default:
      // Fallback for unknown types - use legacy handler
      if (part.text) {
        return <LegacyTextHandler text={part.text} />;
      }
      return null;
  }
};

// Rotating status messages shown while waiting for response
const THINKING_MESSAGES = [
  "Thinking...",
  "Processing...",
  "Analyzing...",
  "...",
  "Working on it...",
];

// AssistantMessageContent - renders content with rotating status fallback
// Subscribes to message runtime to get real-time updates
// Uses a single container div and swaps content inside to prevent layout shifts
const AssistantMessageContent = () => {
  const messageRuntime = useMessageRuntime();
  const [content, setContent] = useState(() => messageRuntime.getState()?.content || []);
  const [thinkingIndex, setThinkingIndex] = useState(0);

  // Subscribe to message updates
  useEffect(() => {
    const unsubscribe = messageRuntime.subscribe(() => {
      const state = messageRuntime.getState();
      if (state?.content) {
        setContent(state.content);
      }
    });
    return unsubscribe;
  }, [messageRuntime]);

  // Check if content is empty or only has empty text
  const hasContent = content && content.length > 0 && content.some(part => {
    if (part.type === 'text') return part.text && part.text.trim().length > 0;
    return true; // Non-text parts always count as content
  });

  // Rotate through thinking messages every 2 seconds when no content
  useEffect(() => {
    if (hasContent) {
      setThinkingIndex(0); // Reset when content arrives
      return;
    }

    const interval = setInterval(() => {
      setThinkingIndex(prev => (prev + 1) % THINKING_MESSAGES.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [hasContent]);

  return (
    <div className="assistant-content-container" style={{ minHeight: '24px' }}>
      {!hasContent ? (
        <span style={{ color: 'var(--mui-palette-text-secondary)', fontStyle: 'italic' }} className="thinking-pulse">
          {THINKING_MESSAGES[thinkingIndex]}
        </span>
      ) : (
        content.map((part, index) => (
          <PartRenderer key={index} part={part} />
        ))
      )}
    </div>
  );
};


const AssistantMessage = () => {
  const [copied, setCopied] = useState(false);
  const embeddedConfig = useContext(EmbeddedConfigContext);

  const handleCopy = async (e) => {
    e.stopPropagation();
    const messageRoot = e.currentTarget.closest('[data-role="assistant"]');
    const contentElem = messageRoot.querySelector('.chat-assistant-text');
    const success = await copyMessageContent(contentElem);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!confirm('Delete this message?')) return;

    const messageRoot = e.currentTarget.closest('[data-role="assistant"]');
    const messageId = messageRoot.getAttribute('data-message-id');
    if (!messageId) {
      alert('Cannot delete message - ID not found');
      return;
    }

    try {
      const API_URL = embeddedConfig?.apiUrl || window.INSIGHT_CONFIG?.API_URL || 'http://localhost:8000';

      // Use different endpoint and auth for embedded vs full mode
      let response;
      if (embeddedConfig?.isEmbedded && embeddedConfig?.shareToken && embeddedConfig?.customGptId) {
        // Embedded mode: use public endpoint with share token
        response = await fetch(`${API_URL}/public/cme/${embeddedConfig.customGptId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'messages.delete',
            params: { message_id: messageId, mode: embeddedConfig.mode || 'full' },
            token: embeddedConfig.shareToken
          })
        });
      } else {
        // Full mode: use protected endpoint with auth token
        const token = localStorage.getItem('cme_token');
        response = await fetch(`${API_URL}/cme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            type: 'messages.delete',
            params: { message_id: messageId, mode: 'full' }
          })
        });
      }

      if (response.ok) {
        messageRoot.remove();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.error || 'Failed to delete message');
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      alert('Failed to delete message');
    }
  };

  return (
    <MessagePrimitive.Root
      style={{ display: 'flex' }}
      className="chat-message-enter chat-message assistant-message"
      data-role="assistant"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box
          className="chat-assistant-text aui-md"
          sx={{
            color: 'text.primary',
            maxWidth: 'none',
            wordBreak: 'break-word',
          }}
        >
          <AssistantMessageContent />
        </Box>
        {/* Copy and Delete buttons - always show in non-embedded mode */}
        <Box
          className="message-tools-container"
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-start',
            gap: 0.5,
            mt: 0.5,
          }}
        >
          {(embeddedConfig?.showCopyButton !== false) && (
            <Tooltip title={copied ? "Copied!" : "Copy message"}>
              <IconButton
                onClick={handleCopy}
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
              >
                {copied ? <CheckIcon /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
          )}
          {(embeddedConfig?.showDeleteButton !== false) && (
            <Tooltip title="Delete message">
              <IconButton
                onClick={handleDelete}
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </MessagePrimitive.Root>
  );
};

const UserMessage = () => {
  const messageFilesContext = useContext(MessageFilesContext);
  const { messageFiles } = messageFilesContext || { messageFiles: [] };
  const messageRef = useRef(null);
  const [messageIndex, setMessageIndex] = useState(0);
  const embeddedConfig = useContext(EmbeddedConfigContext);
  const API_URL = embeddedConfig?.apiUrl || window.INSIGHT_CONFIG?.API_URL || 'http://localhost:8000';

  // Calculate message index on mount and when messages change
  useEffect(() => {
    if (messageRef.current) {
      const allUserMessages = document.querySelectorAll('[data-role="user"]');
      const index = Array.from(allUserMessages).indexOf(messageRef.current);
      setMessageIndex(index);
    }
  }, [messageFiles]);

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!confirm('Delete this message?')) return;

    const messageRoot = e.currentTarget.closest('[data-role="user"]');
    const messageId = messageRoot.getAttribute('data-message-id');
    if (!messageId) {
      alert('Cannot delete message - ID not found');
      return;
    }

    try {
      const API_URL = embeddedConfig?.apiUrl || window.INSIGHT_CONFIG?.API_URL || 'http://localhost:8000';

      // Use different endpoint and auth for embedded vs full mode
      let response;
      if (embeddedConfig?.isEmbedded && embeddedConfig?.shareToken && embeddedConfig?.customGptId) {
        // Embedded mode: use public endpoint with share token
        response = await fetch(`${API_URL}/public/cme/${embeddedConfig.customGptId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'messages.delete',
            params: { message_id: messageId, mode: embeddedConfig.mode || 'full' },
            token: embeddedConfig.shareToken
          })
        });
      } else {
        // Full mode: use protected endpoint with auth token
        const token = localStorage.getItem('cme_token');
        response = await fetch(`${API_URL}/cme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            type: 'messages.delete',
            params: { message_id: messageId, mode: 'full' }
          })
        });
      }

      if (response.ok) {
        messageRoot.remove();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.error || 'Failed to delete message');
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      alert('Failed to delete message');
    }
  };

  // Get files for this message
  const files = messageFiles && messageFiles[messageIndex] ? messageFiles[messageIndex] : [];

  return (
    <MessagePrimitive.Root
      ref={messageRef}
      style={{ display: 'flex', justifyContent: 'flex-end' }}
      className="chat-message-enter chat-message user-message"
      data-role="user"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '75%' }}>
        <Paper
          elevation={0}
          sx={{
            bgcolor: 'action.hover',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          {/* File attachments - render before text like nnaico */}
          {files.length > 0 && (
            <Box sx={{ p: 1.5, pb: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {files.map((att, index) => {
                const isImage = att.content_type && att.content_type.startsWith('image/');
                // Use blob URL directly if available, otherwise construct API URL
                const imageUrl = att.id?.startsWith('blob:') ? att.id : `${API_URL}/attachments/${att.id}`;

                if (isImage) {
                  return (
                    <Box key={index} sx={{ overflow: 'hidden', borderRadius: 3 }}>
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={att.filename}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          objectFit: 'contain',
                          maxWidth: 250,
                          maxHeight: 200,
                        }}
                      />
                    </Box>
                  );
                } else {
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'primary.main',
                        p: 1,
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2">{att.filename}</Typography>
                    </Box>
                  );
                }
              })}
            </Box>
          )}

          <Box
            className="chat-user-text"
            sx={{
              color: 'text.primary',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              p: 1.5,
            }}
          >
            <MessagePrimitive.Parts />
          </Box>
        </Paper>
        {/* Tools container - always render for consistent spacing */}
        <Box
          className="message-tools-container"
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-end',
            gap: 0.5,
            mt: 0.5,
          }}
        >
          {(embeddedConfig?.showDeleteButton !== false) && (
            <Tooltip title="Delete message">
              <IconButton
                onClick={handleDelete}
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </MessagePrimitive.Root>
  );
};
